import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IdExpressApplicationLogo } from "@id-express/global-user-navigation";

import {
  ApplicationPermissionView,
  fetchPermissionApplications,
  fetchPermissionGrants,
  mergeApplicationsAndGrants,
  permissionDisplayLabel,
  updatePermissions,
} from "../../applicationPermissions";
import "./ApplicationPermissions.css";


type Props = {
  ownAppId: string;
  onMessage?: (tone: "success" | "error", message: string) => void;
};


const ApplicationPermissions: React.FC<Props> = ({ ownAppId, onMessage }) => {
  const { t, i18n } = useTranslation();
  const [services, setServices] = useState<ApplicationPermissionView[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [savingAppId, setSavingAppId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [applications, grants] = await Promise.all([
        fetchPermissionApplications(),
        fetchPermissionGrants(),
      ]);
      const merged = mergeApplicationsAndGrants(applications, grants, ownAppId);
      setServices(merged);
      setDrafts(Object.fromEntries(merged.map((service) => [service.appId, service.permissions])));
    } catch {
      onMessage?.("error", t("personalData.applicationAccess.loadError", {
        defaultValue: "Could not load application access. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  }, [onMessage, ownAppId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const dateFormatter = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }),
    [i18n.language]
  );

  const toggle = (appId: string, permission: string) => {
    setDrafts((current) => {
      const selected = new Set(current[appId] || []);
      if (selected.has(permission)) selected.delete(permission);
      else selected.add(permission);
      return { ...current, [appId]: Array.from(selected).sort() };
    });
  };

  const save = async (service: ApplicationPermissionView, permissions: string[]) => {
    setSavingAppId(service.appId);
    try {
      await updatePermissions({ appId: service.appId, permissions });
      onMessage?.(
        "success",
        permissions.length
          ? t("personalData.applicationAccess.saved", {
              app: service.name,
              defaultValue: "Access for {{app}} has been updated.",
            })
          : t("personalData.applicationAccess.revoked", {
              app: service.name,
              defaultValue: "Access for {{app}} has been revoked.",
            })
      );
      await load();
    } catch {
      onMessage?.("error", t("personalData.applicationAccess.saveError", {
        defaultValue: "Could not update application access. Please try again.",
      }));
    } finally {
      setSavingAppId(null);
    }
  };

  if (loading) {
    return <p className="pd-access-status">{t("personalData.applicationAccess.loading", { defaultValue: "Loading application access..." })}</p>;
  }

  if (!services.length) {
    return <p className="pd-access-status">{t("personalData.applicationAccess.empty", { defaultValue: "No applications have requested access to your personal data yet." })}</p>;
  }

  return (
    <div className="pd-access-list">
      {services.map((service) => {
        const selected = drafts[service.appId] || [];
        const changed = JSON.stringify([...selected].sort()) !== JSON.stringify([...service.permissions].sort());
        return (
          <article className="pd-access-card" key={service.appId}>
            <header className="pd-access-card__header">
              <span className="pd-access-card__logo">
                <IdExpressApplicationLogo
                  name={service.name}
                  src={service.urlImage}
                  imageClassName="pd-access-card__logo-image"
                  fallbackClassName="pd-access-card__logo-fallback"
                />
              </span>
              <div>
                <h3>{service.name}</h3>
                {service.permissionsUpdatedAt && (
                  <p>
                    {t("personalData.applicationAccess.updatedAt", {
                      date: dateFormatter.format(new Date(service.permissionsUpdatedAt)),
                      defaultValue: "Updated {{date}}",
                    })}
                  </p>
                )}
              </div>
            </header>

            <div className="pd-access-card__fields">
              {service.allowedPermissions.map((permission) => (
                <label key={permission}>
                  <input
                    type="checkbox"
                    checked={selected.includes(permission)}
                    onChange={() => toggle(service.appId, permission)}
                  />
                  <span>{permissionDisplayLabel(permission, t)}</span>
                </label>
              ))}
            </div>

            <div className="pd-access-card__actions">
              <button
                type="button"
                className="pd-secondary-button"
                disabled={!changed || savingAppId === service.appId}
                onClick={() => save(service, selected)}
              >
                {t("personalData.actions.saveChanges")}
              </button>
              <button
                type="button"
                className="pd-secondary-button pd-access-card__revoke"
                disabled={!selected.length || savingAppId === service.appId}
                onClick={() => save(service, [])}
              >
                {t("personalData.applicationAccess.revokeAll", { defaultValue: "Revoke all" })}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
};


export default ApplicationPermissions;
